/**
 * PRD 解析命令
 * 从产品需求文档自动生成开发任务
 */

import { Command } from 'commander';
import fs from 'fs-extra';
import path from 'path';
import { logger } from '../utils/logger';
import { withErrorHandling } from '../utils/error-handler';
import { PRDParser } from '../core/prd-parser';
import { TaskStore } from '../core/task-store';
import { WorkflowManager } from '../core/workflow-manager';
import { formatTaskList } from '../utils/formatters';
import { colors } from '../utils/colors';
import { Table } from 'console-table-printer';

export function createParsePRDCommand(): Command {
  const command = new Command('parse-prd');
  
  command
    .description('Parse PRD (Product Requirements Document) and generate tasks')
    .argument('<file>', 'Path to PRD file (markdown or text)')
    .option('--auto-create', 'Automatically create tasks without confirmation', false)
    .option('--priority <priority>', 'Default priority for tasks', 'medium')
    .option('--tag <tag>', 'Add tag to all generated tasks')
    .option('--prefix <prefix>', 'Add prefix to all task titles')
    .option('--dry-run', 'Preview tasks without creating them', false)
    .option('--output <file>', 'Save tasks to JSON file')
    .action(withErrorHandling(async (file: string, options) => {
      // 读取 PRD 文件
      const prdPath = path.resolve(file);
      if (!await fs.pathExists(prdPath)) {
        logger.error(`PRD file not found: ${prdPath}`);
        process.exit(1);
      }
      
      const content = await fs.readFile(prdPath, 'utf-8');
      logger.info(`Parsing PRD from: ${prdPath}`);
      
      // 解析 PRD
      const parser = new PRDParser();
      const result = await parser.parsePRD(content);
      
      // 显示解析结果
      console.log(colors.header('\n📄 PRD Analysis Result\n'));
      console.log(colors.info(`Title: ${result.title}`));
      console.log(colors.dim(`Overview: ${result.overview.substring(0, 100)}...`));
      console.log();
      
      // 显示统计信息
      const statsTable = new Table({
        columns: [
          { name: 'metric', title: 'Metric', alignment: 'left' },
          { name: 'value', title: 'Value', alignment: 'right' }
        ]
      });
      
      statsTable.addRow({ metric: 'Total Requirements', value: result.requirements.length });
      statsTable.addRow({ metric: 'Generated Tasks', value: result.tasks.length });
      statsTable.addRow({ metric: 'Estimated Hours', value: `${result.metadata.estimatedHours}h` });
      statsTable.addRow({ metric: 'High Complexity', value: result.metadata.complexity.high });
      statsTable.addRow({ metric: 'Medium Complexity', value: result.metadata.complexity.medium });
      statsTable.addRow({ metric: 'Low Complexity', value: result.metadata.complexity.low });
      
      statsTable.printTable();
      console.log();
      
      // 显示需求列表
      if (result.requirements.length > 0) {
        console.log(colors.header('📋 Parsed Requirements:\n'));
        
        const reqTable = new Table({
          columns: [
            { name: 'type', title: 'Type', alignment: 'center', colored: true },
            { name: 'title', title: 'Title', alignment: 'left', maxLen: 40 },
            { name: 'priority', title: 'Priority', alignment: 'center', colored: true },
            { name: 'complexity', title: 'Complex', alignment: 'center' }
          ]
        });
        
        for (const req of result.requirements) {
          reqTable.addRow({
            type: req.type.toUpperCase(),
            title: req.title.length > 40 ? req.title.substring(0, 37) + '...' : req.title,
            priority: req.priority.toUpperCase(),
            complexity: req.complexity || 5
          });
        }
        
        reqTable.printTable();
        console.log();
      }
      
      // 应用选项到任务
      let tasks = result.tasks;
      if (options.prefix) {
        tasks = tasks.map(task => ({
          ...task,
          content: `${options.prefix} ${task.content}`
        }));
      }
      
      if (options.tag) {
        const tags = options.tag.split(',').map((t: string) => t.trim());
        tasks = tasks.map(task => ({
          ...task,
          metadata: {
            ...task.metadata,
            tags: tags
          }
        }));
      }
      
      // 保存到文件（如果指定）
      if (options.output) {
        const outputPath = path.resolve(options.output);
        await fs.writeJson(outputPath, {
          prd: {
            title: result.title,
            overview: result.overview,
            source: prdPath
          },
          requirements: result.requirements,
          tasks: tasks,
          metadata: result.metadata,
          generatedAt: new Date().toISOString()
        }, { spaces: 2 });
        
        console.log(colors.success(`✅ Tasks saved to: ${outputPath}`));
      }
      
      // 预览模式
      if (options.dryRun) {
        console.log(colors.header('\n🔍 Preview Mode - Tasks will NOT be created\n'));
        console.log(colors.dim('Generated tasks:'));
        console.log(formatTaskList(tasks));
        return;
      }
      
      // 确认创建任务
      if (!options.autoCreate) {
        const { confirm } = await import('inquirer').then(m => m.default.prompt([{
          type: 'confirm',
          name: 'confirm',
          message: `Create ${tasks.length} tasks from PRD?`,
          default: true
        }]));
        
        if (!confirm) {
          console.log(colors.warning('Task creation cancelled'));
          return;
        }
      }
      
      // 创建任务
      const taskStore = new TaskStore();
      const workflowManager = new WorkflowManager();
      
      for (const task of tasks) {
        await taskStore.addTask(task);
        logger.info(`Created task: ${task.content}`);
      }
      
      console.log(colors.success(`\n✅ Created ${tasks.length} tasks from PRD`));
      
      // 显示后续步骤
      console.log(colors.header('\n📌 Next Steps:\n'));
      console.log('1. Review tasks: ' + colors.command('workflow task list'));
      console.log('2. Start working: ' + colors.command('workflow task start'));
      console.log('3. Or use orchestration: ' + colors.command('workflow orchestrate start'));
      
      // 推荐工作流程
      if (result.metadata.complexity.high > 0) {
        console.log(colors.warning('\n⚠️  High complexity tasks detected. Consider:'));
        console.log('- Breaking them down further: ' + colors.command('workflow task expand <id>'));
        console.log('- Assigning to senior developers: ' + colors.command('workflow team assign <id>'));
      }
    }));
  
  return command;
}